// Generated by Claude (Claude Code): https://claude.ai/code/session_01XjQBvV5vxzvd6TGZribDqy
// Made client-neutral by an AI coding agent (Cursor Cloud Agent), 2026-10-08.
//
// Two jobs:
// - ?request=<id>: the OAuth consent screen a staff member sees when adding
//   the staff MCP server to an MCP client (Claude, Cursor, ChatGPT, VS Code,
//   ...). Approving here is what lets that client act as this staff member
//   (reads, and drafts for review).
// - no request: the staff member's live MCP connections, with Revoke.
//
// The client name is whatever the client sent at registration, which is
// unauthenticated, so the page never trusts it: the redirect host is the
// field staff should check.
import { useSearch } from "wouter";
import { format } from "date-fns";
import { useQueryClient } from "@tanstack/react-query";
import {
  getListOAuthConnectionsQueryKey,
  useApproveOAuthConsentRequest,
  useDenyOAuthConsentRequest,
  useGetOAuthConsentRequest,
  useListOAuthConnections,
  useRevokeOAuthConnection,
} from "@workspace/api-client-react";
import StaffLayout from "@/components/StaffLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { AlertTriangle, Bot, ShieldCheck } from "lucide-react";

const LOOPBACK_HOST = /^(localhost|127\.0\.0\.1|\[::1\])(:\d+)?$/i;

function ConsentCard({ requestId }: { requestId: string }) {
  const { toast } = useToast();
  // The app's QueryClient does not retry 4xx, so an expired request fails fast.
  const { data: request, isLoading, isError } = useGetOAuthConsentRequest(requestId);
  const go = (redirectTo: string) => {
    window.location.assign(redirectTo);
  };
  const failed = () =>
    toast({
      title: "This request is no longer valid",
      description: "Start the connection again from your MCP client.",
      variant: "destructive",
    });
  const approve = useApproveOAuthConsentRequest({
    mutation: { onSuccess: (d) => go(d.redirectTo), onError: failed },
  });
  const deny = useDenyOAuthConsentRequest({
    mutation: { onSuccess: (d) => go(d.redirectTo), onError: failed },
  });
  const busy = approve.isPending || deny.isPending;

  if (isLoading) return <Skeleton className="h-56 w-full" />;
  if (isError || !request) {
    return (
      <Card data-testid="consent-invalid">
        <CardHeader>
          <CardTitle>Connection request expired</CardTitle>
          <CardDescription>
            This link is invalid, was already used, or is more than 10 minutes old. Start the connection again from
            your MCP client.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const loopback = LOOPBACK_HOST.test(request.redirectHost);

  return (
    <Card data-testid="consent-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5" /> Connect an MCP client to the MOTUS Bug Board?
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <Alert variant="destructive" data-testid="consent-warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Only approve if you just started this connection yourself</AlertTitle>
          <AlertDescription>
            If you did not just add this server in an MCP client, choose Deny. Someone may have sent you this link to
            get access as you.
          </AlertDescription>
        </Alert>
        <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2">
          <dt className="font-medium text-slate-600 dark:text-slate-300">Sends access to</dt>
          <dd>
            <span className="font-mono font-semibold" data-testid="consent-host">
              {request.redirectHost}
            </span>
            <span className="block text-xs text-slate-500" data-testid="consent-host-hint">
              {loopback
                ? "An app running on your own computer. Any program on it could be posing as this app, so check that you just started it."
                : "Check that this is the website of the app you are connecting."}
            </span>
          </dd>
          <dt className="font-medium text-slate-600 dark:text-slate-300">App name</dt>
          <dd>
            <span data-testid="consent-client">{request.clientName}</span>
            <span className="block text-xs text-slate-500" data-testid="consent-client-hint">
              Self-reported by the app and not verified. Any app can claim any name.
            </span>
          </dd>
          <dt className="font-medium text-slate-600 dark:text-slate-300">Acting as</dt>
          <dd data-testid="consent-staff">{request.staffName}</dd>
        </dl>
        <p className="text-slate-600 dark:text-slate-300">
          It will be able to read reports, including submitter contact details, and draft replies and changes. Drafts
          do nothing until a staff member applies them on the Drafts page. You can revoke access here at any time.
        </p>
        <div className="flex gap-2">
          <Button disabled={busy} onClick={() => approve.mutate({ id: requestId })} data-testid="consent-approve">
            Allow
          </Button>
          <Button variant="outline" disabled={busy} onClick={() => deny.mutate({ id: requestId })} data-testid="consent-deny">
            Deny
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Connections() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data: connections, isLoading } = useListOAuthConnections();
  const revoke = useRevokeOAuthConnection({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListOAuthConnectionsQueryKey() });
        toast({ title: "Connection revoked" });
      },
      onError: () => toast({ title: "Couldn't revoke this connection", variant: "destructive" }),
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bot className="w-5 h-5" /> Your MCP connections
        </CardTitle>
        <CardDescription data-testid="connections-instructions">
          To connect, add a remote MCP server (sometimes called a custom connector) in your MCP client with the URL{" "}
          <code className="text-xs">{`${window.location.origin}/api/mcp`}</code>, then sign in and approve it here.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : !connections || connections.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="connections-empty">No active connections.</p>
        ) : (
          <ul className="divide-y">
            {connections.map((c) => (
              <li key={c.id} className="flex items-center justify-between py-2" data-testid={`connection-${c.id}`}>
                <div className="text-sm">
                  <div className="font-medium">
                    {c.clientName} <span className="text-xs font-normal text-slate-500">(self-reported name)</span>
                  </div>
                  <div className="text-slate-500 text-xs">
                    Connected {format(new Date(c.connectedAt), "MMM d, yyyy")} · last refreshed{" "}
                    {format(new Date(c.lastRefreshedAt), "MMM d, h:mm a")}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={revoke.isPending}
                  onClick={() => revoke.mutate({ id: c.id })}
                  data-testid={`connection-${c.id}-revoke`}
                >
                  Revoke
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

export default function StaffMcpConnectionsPage() {
  const requestId = new URLSearchParams(useSearch()).get("request");
  return (
    <StaffLayout>
      <div className="max-w-xl mx-auto space-y-6">
        {requestId ? <ConsentCard requestId={requestId} /> : <Connections />}
      </div>
    </StaffLayout>
  );
}
